"use client";

import * as React from "react";
import { Activity, Radio, Cpu, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
const applicationsData = [{
  title: "In-Process Blend Monitoring",
  tagline: "Homogeneity & Endpoint Detection",
  description: "Real-time spectroscopic monitoring of active pharmaceutical ingredients (APIs) and excipient blends during batch mixing, eliminating off-line sampling delays.",
  icon: Activity,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/blend_monitoring_application.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/blend_monitoring_application.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/blend_monitoring_application.png-wvc-sizes",
    alt: "In-process blend monitoring instrumentation installed on a pharmaceutical blender"
  },
  points: ["Real-time blend uniformity assessment", "Direct integration with rotating tumble blenders", "Non-destructive spectroscopic measurement"],
  link: "/BlendUniformityPage"
}, {
  title: "Wireless Process Installations",
  tagline: "Cable-Free Rotating Equipment Integration",
  description: "Battery-powered and wireless analytical sensor integration designed for difficult-to-cable vessels, bin blenders, and cleanroom production environments.",
  icon: Radio,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/wireless_process_application.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/wireless_process_application.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/wireless_process_application.png-wvc-sizes",
    alt: "Wireless analytical sensor unit mounted on processing equipment"
  },
  points: ["No slip rings or trailing cables required", "Long-life onboard power and secure wireless data", "Suitable for sanitary and regulated cleanrooms"],
  link: "/ProcessAnalyticalTechnologyPage"
}, {
  title: "Continuous Manufacturing Integration",
  tagline: "Inline Real-Time Quality Assurance",
  description: "High-speed inline analytical monitoring for continuous direct compression, extrusion, and fluid-bed operations with automated feedback control readiness.",
  icon: Cpu,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/continuous_manufacturing_application.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/continuous_manufacturing_application.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/continuous_manufacturing_application.png-wvc-sizes",
    alt: "Continuous manufacturing line analytical interface and probe assembly"
  },
  points: ["Sub-second data acquisition rates", "Interface to standard industrial control architectures", "Compliant data recording for real-time release"],
  link: "/SolutionsPage"
}];
export default function Applications() {
  const shouldReduceMotion = useReducedMotion();
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        delay: shouldReduceMotion ? 0 : i * 0.15,
        ease: "easeOut"
      }
    })
  };
  return <section id="applications" data-nav="light" className="w-full bg-background py-16 md:py-24 border-b border-border">
      <div className="page-container">
        {/* Header Block */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-4">
            Industrial Implementations
          </Badge>
          <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4">
            Typical Analytical Applications
          </h2>
          <p className="font-default text-base md:text-lg text-muted-foreground leading-relaxed">
            Proven process analytical technology implementations supporting regulated pharmaceutical, biopharmaceutical, and chemical manufacturing workflows across Ireland and the UK.
          </p>
        </div>

        {/* 3-Column Applications Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {applicationsData.map((item, i) => {
          const IconComponent = item.icon;
          return <motion.div key={item.title} data-index={i} custom={i} initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: "-40px"
          }} variants={cardVariants} className="flex flex-col h-full">
                <Card className="h-full flex flex-col justify-between border-border bg-card shadow-sm hover:shadow-md transition-shadow duration-200">
                  <div>
                    {/* Visual / Image Block */}
                    <div className="relative aspect-16/10 w-full overflow-hidden rounded-t-xl bg-muted border-b border-border">
                      <img src={item.image.src} data-wvc-srcset={item.image.srcset} data-wvc-sizes={item.image.sizes} alt={item.image.alt} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                      <div className="absolute top-3 left-3 bg-card/90 backdrop-blur-xs p-2 rounded-md shadow-xs border border-border">
                        <IconComponent className="size-5 text-primary" aria-hidden="true" />
                      </div>
                    </div>

                    <CardHeader className="pt-6 pb-3">
                      <span className="text-xs font-semibold tracking-wider uppercase text-primary mb-1">
                        {item.tagline}
                      </span>
                      <CardTitle className="text-xl md:text-2xl font-bold text-card-foreground">
                        {item.title}
                      </CardTitle>
                      <CardDescription className="text-muted-foreground text-sm leading-relaxed mt-2">
                        {item.description}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="pt-2 pb-4">
                      <ul className="space-y-2.5">
                        {item.points.map((point, pointIndex) => <li key={pointIndex} data-index={pointIndex} className="flex items-start gap-2.5 text-xs md:text-sm text-foreground/90">
                            <CheckCircle2 className="size-4 shrink-0 text-primary mt-0.5" aria-hidden="true" />
                            <span>{point}</span>
                          </li>)}
                      </ul>
                    </CardContent>
                  </div>

                  <CardFooter className="pt-2 pb-6">
                    <Button asChild variant="outline" className="w-full justify-between">
                      <Link to={item.link}>
                        <span>Application details</span>
                        <ArrowRight className="size-4 ml-2" aria-hidden="true" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Bottom Consultation Banner */}
        <div className="mt-12 md:mt-16 bg-secondary rounded-xl p-6 md:p-8 border border-border flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="max-w-2xl">
            <h3 className="font-default text-lg md:text-xl font-semibold text-secondary-foreground mb-1">
              Have a specific process requirement?
            </h3>
            <p className="font-default text-sm text-muted-foreground">
              Our technical specialists evaluate sample compatibility, probe placement, and integration interfaces for your facility.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <Button asChild variant="default">
              <Link to="/DiscussYourApplicationPage">Discuss your process</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/FeasibilityStudiesPage">Feasibility studies</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}